import {Breadcrumbs, Button, ButtonCell, Headline, Modal, Section} from "@telegram-apps/telegram-ui";
import {Link} from "@/components/Link/Link.tsx";
import {useTranslation} from "react-i18next";
import {Wish} from "@/api/types.ts";
import {useEffect, useState} from "react";
import {Icon28AddCircle} from "@telegram-apps/telegram-ui/dist/icons/28/add_circle";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {WishCell} from "@/components/Wish/WishCell.tsx";
import {
  ModalHeader
} from "@telegram-apps/telegram-ui/dist/components/Overlays/Modal/components/ModalHeader/ModalHeader";
import {WishView} from "@/components/Wish/WishView.tsx";
import {useOutletContext, useParams} from "react-router-dom";
import {miniApp, useSignal} from "@telegram-apps/sdk-react";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {WishActionsMenu} from "@/components/Wish/WishActionsMenu.tsx";
import {haptic} from "@/helpers/haptic.ts";

export const AllWishes = () => {
  const { authProfile } = useAuthProfile();
  const { onWishSaved } = useOutletContext<{onWishSaved: () => Promise<void> | undefined}>();

  const { t } = useTranslation();
  const { navigateToRoute } = useNavigation();
  const { wishId } = useParams();
  const [ openedWish, setOpenedWish ] = useState<Wish | null>(null);
  const isDark = useSignal(miniApp.isDark);
  const [ isBottomSheetOpen, setIsBottomSheetOpen ] = useState<boolean>(wishId != null);

  useEffect(() => {
    if (wishId) {
      const wish = authProfile?.allWishes.find(wish => wish.id === wishId);
      if (wish) {
        setOpenedWish(wish);
        return () => {setOpenedWish(null)};
      } else {
        if (isBottomSheetOpen) {
          setIsBottomSheetOpen(false);
        }
        navigateToRoute(`/main/wishes/all/${openedWish?.id}`, true);
      }
    }
  }, [authProfile]);

  const onWishUpdate = async () => {
    await onWishSaved();
    haptic.notification('success');
  }

  const onWishDelete = async () => {
    await onWishSaved();
    haptic.notification('success');
    setOpenedWish(null);
    setIsBottomSheetOpen(false);
    navigateToRoute(`/main/wishes/all`, true);
  }

  const openWish = (wish: Wish) => {
    haptic.impact('soft');
    setOpenedWish(wish);
    setIsBottomSheetOpen(true);
  }

  const bottomSheetOpenChange = (isOpen: boolean) => {
    setIsBottomSheetOpen(isOpen);
    if (!isOpen) {
      navigateToRoute(`/main/wishes/all`, true);
    } else {
      navigateToRoute(`/main/wishes/all/${openedWish?.id}`, true);
    }
  }

  const onWishEditClick = (wish: Wish | null) => {
    if (!wish) {
      return;
    }
    navigateToRoute(`/main/wishes/all/${wish.id}/edit`);
  }

  const getActiveWishCell = (wish: Wish) => {
    if (wish.status === 'active') {
      return <WishCell key={"wish_" + wish.id}
                        wish={wish}
                        onClick={() => openWish(wish)}/>
    }
    return null;
  }

  return (
    <div>
      <div className={"mr-3 ml-3 flex flex-col gap-3"}>
        <Breadcrumbs>
          <Breadcrumbs.Item><Link to={"/main"}>{t('navigation.main')}</Link></Breadcrumbs.Item>
          <Breadcrumbs.Item>
            {t('navigation.allWishes')}
          </Breadcrumbs.Item>
        </Breadcrumbs>

        <div className={"pb-10"}>
          <Section>
            <div className={"flex justify-between p-2 pl-5 pr-0 min-h-10 items-center"}>
              <Headline weight="1">{t('wish.wishes')}</Headline>
              <Button
                onClick={() => {
                  navigateToRoute(`/main/wishes/new`);
                }}
                after={<Icon28AddCircle></Icon28AddCircle>}
                mode={"plain"} size={"s"}>
                {t('action.add')}
              </Button>
            </div>

            {authProfile?.allWishes.map((wish) => (
              getActiveWishCell(wish)
            ))}

            <ButtonCell
              onClick={() => {
                navigateToRoute(`/main/wishes/new`);
              }}
              className={"mb-3 pl-7"}
              before={<Icon28AddCircle />}>
              <div className={"pl-1"}>{t('wish.action.add')}</div>
            </ButtonCell>
          </Section>

          <Modal
            header={<ModalHeader
              after={<WishActionsMenu
                wish={openedWish}
                isOwner={true}
                onUpdate={onWishUpdate}
                onEditClick={() => onWishEditClick(openedWish)}
                onDelete={onWishDelete}
              />}
            >
              {openedWish?.title || 'wish not loaded'}
            </ModalHeader>}
            open={isBottomSheetOpen}
            onOpenChange={bottomSheetOpenChange}
            className={"safe-area-bottom min-h-96"}
            style={{
              backgroundColor: (isDark ? "var(--tgui--section_bg_color)" : "var(--tgui--secondary_bg_color)"),
              maxHeight: "calc(100% - 50px)",
            }}
          >
            <WishView wish={openedWish} onUpdate={onWishUpdate}/>
          </Modal>
        </div>
      </div>
    </div>
  );
}
